import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle, Loader2, Sparkles } from 'lucide-react';

const waitlistSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

type WaitlistFormValues = z.infer<typeof waitlistSchema>;

type SubmitState = 'idle' | 'loading' | 'success' | 'error';

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;

async function postWaitlist(email: string): Promise<void> {
  const url = `${apiBaseUrl ?? ''}/leads/waitlist`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok && response.status !== 204) {
    throw new Error('Request failed');
  }
  if (response.status === 200) {
    const data = (await response.json()) as { status?: string };
    if (data.status !== 'subscribed' && data.status !== 'already_subscribed') {
      throw new Error('Unexpected response');
    }
  }
}

export function WaitlistForm() {
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (values: WaitlistFormValues) => {
    setSubmitState('loading');
    setErrorMessage('');
    try {
      await postWaitlist(values.email);
      setSubmitState('success');
    } catch {
      setSubmitState('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (submitState === 'success') {
    return (
      <div className="flex flex-col items-center gap-4 max-w-md w-full">
        <div
          className="flex flex-col items-center gap-4 p-8 rounded-2xl border border-border bg-card text-card-foreground w-full"
          role="status"
          aria-live="polite"
        >
          <CheckCircle className="h-10 w-10 text-primary" aria-hidden="true" />
          <p className="text-lg font-medium text-center">
            You're on the list. We'll email you the moment we launch.
          </p>
        </div>
        <div className="mt-2 flex items-center justify-center gap-2">
          <span className="text-xs text-muted-foreground">Powered by</span>
          <a
            href="https://vectr.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:opacity-100 opacity-90 transition-opacity"
            aria-label="Visit Vectr.ai"
          >
            <img src="/vectr-icon-dark.svg" alt="Vectr.ai" className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold text-white">Vectr.ai</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col items-center gap-3 w-full max-w-md"
      aria-label="Waitlist signup"
    >
      <div className="w-full">
        <label htmlFor="waitlist-email" className="sr-only">
          Email address
        </label>
        <input
          id="waitlist-email"
          type="email"
          autoComplete="email"
          placeholder="your@email.com"
          aria-describedby={errors.email ? 'waitlist-email-error' : undefined}
          aria-invalid={errors.email ? 'true' : 'false'}
          className="w-full h-14 px-5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background disabled:opacity-50"
          disabled={submitState === 'loading'}
          {...register('email')}
        />
        {errors.email && (
          <p
            id="waitlist-email-error"
            role="alert"
            className="mt-2 text-sm text-destructive"
          >
            {errors.email.message}
          </p>
        )}
        {submitState === 'error' && !errors.email && (
          <p
            role="alert"
            className="mt-2 text-sm text-destructive"
          >
            {errorMessage}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={submitState === 'loading'}
        className="w-full py-3 px-6 rounded-lg bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-semibold text-base shadow-lg shadow-primary/30 hover:shadow-primary/50 focus:ring-2 focus:ring-primary/60 focus:ring-offset-2 focus-visible:outline-none disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200"
      >
        {submitState === 'loading' ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span>Joining...</span>
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" aria-hidden="true" />
            <span>Join the Waitlist</span>
          </>
        )}
      </button>

      <div className="mt-6 flex items-center justify-center gap-2">
        <span className="text-xs text-muted-foreground">Powered by</span>
        <a
          href="https://vectr.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 hover:opacity-100 opacity-90 transition-opacity"
          aria-label="Visit Vectr.ai"
        >
          <img src="/vectr-icon-dark.svg" alt="Vectr.ai" className="h-3.5 w-3.5" />
          <span className="text-xs font-semibold text-white">Vectr.ai</span>
        </a>
      </div>
    </form>
  );
}
